/*
 * Collection pages: the quick-add cards and the toolbar (snippets/fz-quick-card.liquid,
 * fz-collection-tools.liquid, fz-collection-grid.liquid). Q4 2026, Luke 9 Oct.
 *
 * The cards: the size select IS the form's [name=id], so the variant MonsterCart adds is the one
 * the shopper sees chosen. This file only follows the choice (price, the pre-order line, the
 * quantity cap) and refuses an Add to Cart with no size chosen. MonsterCart binds its click
 * handler on the button itself, so the refusal listens in the CAPTURE phase on the document,
 * which runs first, and stops the event there.
 *
 * The toolbar: Filter by (type, or the clearance page's sizes), Colour (any of a card's families)
 * and the search (every word must appear) combine. Debut's grid floats its cards and clears rows
 * with :nth-child, so a card that does not match is taken out of the list, not hidden, and put
 * back in its place. The state lives in the URL (?type=&colour=&size=&q=&sort_by=).
 *
 * Sort by re-orders IN PLACE (Luke, 25 Oct 2026, A-G02: "every time a different 'sort by' option
 * is chosen on the collections page, it forces a page refresh"): the Section Rendering API returns
 * the section in the new order, and the cards already on the page -- with whatever size and
 * quantity the shopper has chosen on them -- are moved to match, by data-fz-key. If that answer
 * cannot be had, the page goes to the sorted URL, which is what Debut always did.
 */
(function () {
  "use strict";

  var HINT_SIZE = "Pick a size first.";
  var HINT_COLOUR = "Pick a colour first.";

  function closestForm(node) {
    return node && node.closest ? node.closest("[data-fz-form]") : null;
  }

  function cardOf(form) {
    return form.closest("[data-fz-card]");
  }

  function showHint(form, text) {
    var hint = form.querySelector("[data-fz-hint]");
    if (!hint) return;
    hint.textContent = text;
    hint.hidden = !text;
  }

  function chosenOption(select) {
    return select && select.selectedIndex >= 0 ? select.options[select.selectedIndex] : null;
  }

  function onSizeChange(select) {
    var form = closestForm(select);
    var card = form ? cardOf(form) : null;
    var option = chosenOption(select);
    if (!form || !card || !option) return;
    rememberCard(card);
    showHint(form, "");
    if (!option.value) {
      restoreCard(card, form);
      return;
    }
    setSoldOut(form, option.hasAttribute("data-out"));

    var dl = card.querySelector(".price");
    var price = option.getAttribute("data-price") || "";
    var compare = option.getAttribute("data-compare") || "";
    // The money strings were rendered by Liquid's money filter into our own attributes.
    card.querySelectorAll("[data-fz-price], [data-fz-sale]").forEach(function (el) {
      el.innerHTML = price;
    });
    var compareEl = card.querySelector("[data-fz-compare]");
    if (compareEl) compareEl.innerHTML = compare;
    if (dl) dl.classList.toggle("price--on-sale", compare !== "");

    var line = card.querySelector("[data-fz-preorder]");
    var date = option.getAttribute("data-pre") || "";
    if (line) {
      if (date) {
        line.textContent = "Pre-order · ships by " + date;
        line.hidden = false;
      } else if (!line.hasAttribute("data-fz-all-pre")) {
        line.hidden = true;
      }
    }

    var qty = form.querySelector("[data-fz-qty]");
    var max = option.getAttribute("data-max");
    if (qty) {
      if (max) qty.setAttribute("max", max);
      else qty.removeAttribute("max");
      clampQty(qty);
    }
  }

  // "You may also like": the colour select narrows the size select (the form's [name=id]) to that
  // colour's sizes, and the photo and links follow the colour. The list is REBUILT rather than
  // hiding <option>s, because iOS Safari shows hidden options anyway.
  // `keep` is the size to stay on when it belongs to the colour (the server's preselect, on load).
  // Otherwise the colour's first in-stock size is chosen, then its first pre-order size; a size
  // with nothing to sell is skipped and stays listed (never greyed).
  function onColourChange(colourSelect, keep) {
    var form = closestForm(colourSelect);
    var card = form ? cardOf(form) : null;
    var size = form ? form.querySelector("[data-fz-size]") : null;
    if (!form || !card || !size) return;
    rememberCard(card);
    if (!size.__fzAll) size.__fzAll = Array.prototype.slice.call(size.options);
    var colour = colourSelect.value;
    while (size.options.length) size.remove(0);
    var pick = null;
    size.__fzAll.forEach(function (o) {
      if (o.getAttribute("data-colour") !== colour) return;
      o.selected = false;
      size.add(o);
      if (keep && o.value === keep) pick = o;
    });
    // As the server chooses (L-114 B, Luke 15 Oct): the colour's first in-stock ADULT size
    // (Medium, then Large; the list runs small to large), else Kids/Small in stock, else the first
    // adult pre-order, else Kids/Small's pre-order.
    var ranked = [null, null, null, null];
    for (var i = 0; i < size.options.length; i++) {
      var o = size.options[i];
      if (o.hasAttribute("data-out")) continue;
      var kid = o.getAttribute("data-rank") === "1";
      var slot = o.getAttribute("data-pre") ? (kid ? 3 : 2) : kid ? 1 : 0;
      if (!ranked[slot]) ranked[slot] = o;
    }
    if (!pick) pick = ranked[0] || ranked[1] || ranked[2] || ranked[3];
    if (!pick && size.options.length) pick = size.options[0];
    size.disabled = false;
    restoreCard(card, form);
    showHint(form, "");
    if (pick) {
      pick.selected = true;
      size.value = pick.value;
      onSizeChange(size);
    }
    var chosen = chosenOption(colourSelect);
    var img = card.querySelector(".primary-image");
    var src = chosen && chosen.getAttribute("data-img");
    if (img && src) {
      img.removeAttribute("srcset");
      img.removeAttribute("data-srcset");
      img.setAttribute("data-src", src);
      img.src = src;
      img.classList.remove("lazyload");
      img.classList.add("lazyloaded");
    }
    var url = chosen && chosen.getAttribute("data-url");
    if (url) {
      card.querySelectorAll("a[href]").forEach(function (a) {
        a.setAttribute("href", url);
      });
    }
  }

  // Remembers what the server rendered, before the first change.
  function rememberCard(card) {
    if (card.hasAttribute("data-fz-remembered")) return;
    card.setAttribute("data-fz-remembered", "");
    card.querySelectorAll("[data-fz-price], [data-fz-sale], [data-fz-compare]").forEach(function (el) {
      el.setAttribute("data-fz-initial", el.innerHTML);
    });
    var dl = card.querySelector(".price");
    if (dl) dl.setAttribute("data-fz-initial-sale", dl.classList.contains("price--on-sale") ? "true" : "false");
  }

  // A size with nothing to sell is chosen like any other (Luke, 26 Sep: never grey out a size);
  // the button says so instead, as on the product page.
  function setSoldOut(form, soldOut) {
    var button = form.querySelector("[data-add-to-cart]");
    if (!button) return;
    if (!button.hasAttribute("data-fz-label")) button.setAttribute("data-fz-label", button.textContent);
    button.disabled = soldOut;
    button.textContent = soldOut ? "Sold out" : button.getAttribute("data-fz-label");
  }

  // Back on "Select size": the card as the server rendered it.
  function restoreCard(card, form) {
    setSoldOut(form, false);
    card.querySelectorAll("[data-fz-initial]").forEach(function (el) {
      el.innerHTML = el.getAttribute("data-fz-initial");
    });
    var dl = card.querySelector(".price");
    if (dl && dl.hasAttribute("data-fz-initial-sale")) {
      dl.classList.toggle("price--on-sale", dl.getAttribute("data-fz-initial-sale") === "true");
    }
    var line = card.querySelector("[data-fz-preorder]");
    if (line && !line.hasAttribute("data-fz-all-pre")) line.hidden = true;
    var qty = form.querySelector("[data-fz-qty]");
    if (qty) qty.removeAttribute("max");
  }

  function clampQty(qty) {
    var value = parseInt(qty.value, 10);
    if (!isFinite(value) || value < 1) value = 1;
    var max = parseInt(qty.getAttribute("max"), 10);
    if (isFinite(max) && max > 0 && value > max) value = max;
    if (String(value) !== qty.value) qty.value = String(value);
    return value;
  }

  // Returns true when the form may be added as it stands.
  function guard(form, event) {
    var select = form.querySelector("[data-fz-size]");
    var colour = form.querySelector("[data-fz-colour-pick]");
    var qty = form.querySelector("[data-fz-qty]");
    if (qty) clampQty(qty);
    if (colour && !colour.value) {
      event.preventDefault();
      event.stopImmediatePropagation();
      event.stopPropagation();
      showHint(form, colour.getAttribute("data-hint") || HINT_COLOUR);
      colour.focus();
      return false;
    }
    var chosen = chosenOption(select);
    if (chosen && chosen.hasAttribute("data-out")) {
      event.preventDefault();
      event.stopImmediatePropagation();
      event.stopPropagation();
      setSoldOut(form, true);
      return false;
    }
    if (select && !select.value) {
      event.preventDefault();
      event.stopImmediatePropagation();
      event.stopPropagation();
      showHint(form, HINT_SIZE);
      select.focus();
      return false;
    }
    showHint(form, "");
    return true;
  }

  document.addEventListener(
    "click",
    function (event) {
      var button = event.target && event.target.closest ? event.target.closest("[data-add-to-cart]") : null;
      var form = closestForm(button);
      if (form) guard(form, event);
    },
    true,
  );

  document.addEventListener(
    "submit",
    function (event) {
      var form = event.target;
      if (form && form.hasAttribute && form.hasAttribute("data-fz-form")) guard(form, event);
    },
    true,
  );

  document.addEventListener("change", function (event) {
    var target = event.target;
    if (!target || !target.matches) return;
    if (target.matches("[data-fz-colour-pick]")) onColourChange(target, null);
    else if (target.matches("[data-fz-size]")) onSizeChange(target);
    else if (target.matches("[data-fz-qty]")) clampQty(target);
  });

  // ---- The toolbar -------------------------------------------------------------------------

  function normalise(text) {
    var out = String(text || "").toLowerCase();
    if (out.normalize) out = out.normalize("NFD").replace(/[̀-ͯ]/g, "");
    return out
      .replace(/gray/g, "grey")
      .replace(/colou?r/g, "colour")
      .replace(/[^a-z0-9]+/g, " ")
      .trim();
  }

  function words(text) {
    var n = normalise(text);
    return n ? n.split(" ") : [];
  }

  // Reads one card's filter data. A card puts it on itself (data-fz-card); the clearance page's
  // list items carry it on the <li>. The key, which names the card across sorts, is on the <li>.
  function itemFrom(li) {
    var card = li.querySelector("[data-fz-card]") || li;
    var own = function (name) {
      return (li.getAttribute && li.getAttribute(name)) || "";
    };
    return {
      li: li,
      key: own("data-fz-key"),
      type: card.getAttribute("data-type") || "",
      size: card.getAttribute("data-size") || own("data-size"),
      colours: (card.getAttribute("data-colours") || "").split("|").filter(Boolean),
      haystack: " " + normalise(card.getAttribute("data-search") || li.textContent) + " ",
    };
  }

  function matchItem(item, state, terms) {
    if (state.type !== "all" && item.type !== state.type) return false;
    if (state.sizes && state.sizes.length && state.sizes.indexOf(item.size) === -1) return false;
    if (state.colours.length) {
      var hit = false;
      for (var i = 0; i < state.colours.length; i++) {
        if (item.colours.indexOf(state.colours[i]) !== -1) {
          hit = true;
          break;
        }
      }
      if (!hit) return false;
    }
    for (var j = 0; j < terms.length; j++) {
      if (item.haystack.indexOf(terms[j]) === -1) return false;
    }
    return true;
  }

  // ---- Sort by ----

  // The URL the Section Rendering API answers a sort on: this page, in the new order, this section
  // only. The page's own filters stay out of it; they never reach the server.
  function sectionUrl(location, sectionId, sortBy) {
    var params = new URLSearchParams();
    params.set("sort_by", sortBy);
    params.set("section_id", sectionId);
    return location.pathname + "?" + params.toString();
  }

  // The cards' keys in the order an answer lists them.
  function keysIn(html) {
    var keys = [];
    var re = /data-fz-key="([^"]*)"/g;
    var m;
    while ((m = re.exec(html))) keys.push(m[1]);
    return keys;
  }

  // The items in the keys' order; an item the answer did not name keeps its place after them, so
  // a card is never lost to a stale or partial answer.
  function reorder(items, keys) {
    var byKey = {};
    items.forEach(function (item) {
      byKey[item.key] = item;
    });
    var out = [];
    var used = {};
    keys.forEach(function (key) {
      if (byKey.hasOwnProperty(key) && !used[key]) {
        used[key] = true;
        out.push(byKey[key]);
      }
    });
    items.forEach(function (item) {
      if (!used[item.key]) out.push(item);
    });
    return out;
  }

  // Debut sizes each toolbar select to its chosen option on a desktop (slate.utils.resizeSelects,
  // only on the default template's section type, and only at load). The same, for every toolbar
  // select, at load and after each change, so a longer sort name is never cut off.
  function fitSelect(select) {
    if (!select || !window.matchMedia || !window.matchMedia("(min-width: 750px)").matches) return;
    var option = select.options[select.selectedIndex];
    if (!option) return;
    var probe = document.createElement("span");
    probe.textContent = option.label || option.textContent;
    probe.style.cssText = "position:absolute;visibility:hidden;white-space:nowrap;font:" + getComputedStyle(select).font;
    document.body.appendChild(probe);
    select.style.width = probe.offsetWidth + 55 + "px";
    probe.remove();
  }

  // A checkbox panel behind a button: Colour, and the clearance page's sizes. `kind` names its
  // attributes: data-fz-<kind>-toggle, data-fz-<kind>-option, data-fz-<kind>-value.
  function Facet(root, kind, emptyText, manyText) {
    this.box = root.querySelector("[data-fz-" + kind + "]");
    this.toggle = root.querySelector("[data-fz-" + kind + "-toggle]");
    this.panel = this.box ? this.box.querySelector('[role="group"]') : null;
    this.value = root.querySelector("[data-fz-" + kind + "-value]");
    this.n = root.querySelector("[data-fz-" + kind + "-n]");
    this.inputs = Array.prototype.slice.call(root.querySelectorAll("[data-fz-" + kind + "-option]"));
    this.emptyText = emptyText;
    this.manyText = manyText;
  }

  Facet.prototype.chosen = function () {
    return this.inputs
      .filter(function (input) {
        return input.checked;
      })
      .map(function (input) {
        return input.value;
      });
  };

  Facet.prototype.nameOf = function (input) {
    var n = input.parentNode.querySelector(".fz-ct__colour-name");
    return n ? n.textContent : input.value;
  };

  Facet.prototype.set = function (values) {
    this.inputs.forEach(function (input) {
      input.checked = values.indexOf(input.value) !== -1;
    });
  };

  Facet.prototype.render = function () {
    var self = this;
    var names = this.inputs
      .filter(function (input) {
        return input.checked;
      })
      .map(function (input) {
        return self.nameOf(input);
      });
    if (this.n) {
      this.n.textContent = names.length ? "(" + names.length + ")" : "";
      this.n.hidden = !names.length;
    }
    // The box says what is chosen: All, the one value, or how many.
    if (this.value) {
      this.value.textContent =
        names.length === 0 ? this.emptyText : names.length === 1 ? names[0] : this.manyText.replace("{n}", names.length);
    }
  };

  Facet.prototype.open = function (open) {
    if (!this.panel || !this.toggle) return;
    this.panel.hidden = !open;
    this.toggle.setAttribute("aria-expanded", open ? "true" : "false");
  };

  // The controls alone: Filter by, Colour, the sizes, the search, Sort by, the pills and the URL.
  // `onChange` runs on every filter change, `onSort` on a new sort.
  function Controls(root, onChange, onSort) {
    this.root = root;
    this.onChange = onChange;
    this.onSort = onSort;
    this.type = root.querySelector("[data-fz-type]");
    this.colour = new Facet(root, "colour", "All", "{n} selected");
    this.sizes = new Facet(root, "sizes", "All sizes", "{n} sizes");
    this.facets = [this.colour, this.sizes];
    // Kept under its old names for the clearance-era callers and the tests.
    this.colourBox = this.colour.box;
    this.colourToggle = this.colour.toggle;
    this.colourPanel = this.colour.panel;
    this.colourInputs = this.colour.inputs;
    this.search = root.querySelector("[data-fz-search]");
    this.searchClear = root.querySelector("[data-fz-search-clear]");
    this.sort = root.querySelector("[data-fz-sort]");
    this.pills = root.querySelector("[data-fz-pills]");
    this.readUrl();
    this.bind();
  }

  Controls.prototype.readUrl = function () {
    var params = new URLSearchParams(window.location.search);
    var type = (params.get("type") || "").replace(/[^a-z]/g, "");
    if (this.type && type && this.type.querySelector('option[value="' + type + '"]')) {
      this.type.value = type;
    }
    this.colour.set((params.get("colour") || params.get("color") || "").split(",").filter(Boolean));
    this.sizes.set(params.getAll("size"));
    if (this.search) this.search.value = params.get("q") || "";
  };

  Controls.prototype.state = function () {
    return {
      type: this.type ? this.type.value : "all",
      colours: this.colour.chosen(),
      sizes: this.sizes.chosen(),
      query: this.search ? this.search.value : "",
    };
  };

  Controls.prototype.writeUrl = function (state) {
    if (!window.history || !window.history.replaceState) return;
    var params = new URLSearchParams(window.location.search);
    params.delete("color");
    params.delete("size");
    if (state.type && state.type !== "all") params.set("type", state.type);
    else params.delete("type");
    if (state.colours.length) params.set("colour", state.colours.join(","));
    else params.delete("colour");
    (state.sizes || []).forEach(function (size) {
      params.append("size", size);
    });
    if (state.query.trim()) params.set("q", state.query.trim());
    else params.delete("q");
    if (this.sort) {
      var def = this.sort.getAttribute("data-default-sortby") || "";
      if (this.sort.value && this.sort.value !== def) params.set("sort_by", this.sort.value);
      else params.delete("sort_by");
    }
    var search = params.toString();
    var url = window.location.pathname + (search ? "?" + search : "") + window.location.hash;
    window.history.replaceState(window.history.state, "", url);
  };

  // Called after every change: the parts of the controls that show the state.
  Controls.prototype.render = function (state) {
    if (this.searchClear) this.searchClear.hidden = !state.query;
    this.facets.forEach(function (facet) {
      facet.render();
    });
    this.renderPills(state);
  };

  Controls.prototype.renderPills = function (state) {
    if (!this.pills) return;
    var self = this;
    this.pills.innerHTML = "";
    var count = 0;
    this.facets.forEach(function (facet) {
      facet.inputs.forEach(function (input) {
        if (!input.checked) return;
        count++;
        var pill = document.createElement("button");
        pill.type = "button";
        pill.className = "fz-ct__pill";
        pill.setAttribute("data-fz-pill", input.value);
        var label = document.createElement("span");
        label.textContent = facet.nameOf(input);
        var x = document.createElement("span");
        x.className = "fz-ct__pill-x";
        x.setAttribute("aria-hidden", "true");
        x.textContent = "×";
        pill.appendChild(label);
        pill.appendChild(x);
        pill.setAttribute("aria-label", "Remove " + label.textContent);
        pill.__fzInput = input;
        self.pills.appendChild(pill);
      });
    });
    this.pills.hidden = !count;
  };

  Controls.prototype.openColours = function (open) {
    this.colour.open(open);
  };

  Controls.prototype.changed = function () {
    var state = this.state();
    this.render(state);
    this.writeUrl(state);
    this.onChange(state);
  };

  Controls.prototype.reset = function () {
    if (this.type) this.type.value = "all";
    this.facets.forEach(function (facet) {
      facet.set([]);
    });
    if (this.search) this.search.value = "";
    this.changed();
  };

  Controls.prototype.bind = function () {
    var self = this;
    var timer = null;
    if (this.type) {
      this.type.addEventListener("change", function () {
        fitSelect(self.type);
        self.changed();
      });
    }
    if (this.sort) {
      // A keyboard user arrowing through the closed select fires a change per option; each asks
      // for its order and only the last answer is used (Tools.sortTo).
      this.sort.addEventListener("change", function () {
        fitSelect(self.sort);
        self.writeUrl(self.state());
        if (self.onSort) self.onSort(self.sort.value);
      });
      var form = this.sort.closest("[data-fz-sort-form]");
      if (form) {
        form.addEventListener("submit", function (event) {
          event.preventDefault();
          if (self.onSort) self.onSort(self.sort.value);
        });
      }
    }
    if (this.search) {
      this.search.addEventListener("input", function () {
        clearTimeout(timer);
        timer = setTimeout(function () {
          self.changed();
        }, 60);
      });
      this.search.addEventListener("keydown", function (event) {
        if (event.key === "Enter") {
          event.preventDefault();
          self.search.blur();
        } else if (event.key === "Escape" && self.search.value) {
          event.preventDefault();
          self.search.value = "";
          self.changed();
        }
      });
    }
    if (this.searchClear) {
      this.searchClear.addEventListener("click", function () {
        self.search.value = "";
        self.changed();
        self.search.focus();
      });
    }
    this.facets.forEach(function (facet) {
      facet.inputs.forEach(function (input) {
        input.addEventListener("change", function () {
          self.changed();
        });
      });
      if (!facet.toggle || !facet.panel) return;
      facet.toggle.addEventListener("click", function (event) {
        event.stopPropagation();
        var opening = facet.panel.hidden;
        self.facets.forEach(function (other) {
          if (other !== facet) other.open(false);
        });
        facet.open(opening);
      });
      document.addEventListener("click", function (event) {
        if (!facet.box.contains(event.target)) facet.open(false);
      });
      facet.box.addEventListener("keydown", function (event) {
        if (event.key === "Escape" && !facet.panel.hidden) {
          facet.open(false);
          facet.toggle.focus();
        }
      });
    });
    if (this.pills) {
      this.pills.addEventListener("click", function (event) {
        var pill = event.target.closest("[data-fz-pill]");
        if (!pill || !pill.__fzInput) return;
        pill.__fzInput.checked = false;
        self.changed();
      });
    }
  };

  // The quick-add grid: controls plus the cards they filter and sort.
  function Tools(root, grid) {
    var self = this;
    this.root = root;
    this.grid = grid;
    this.items = Array.prototype.slice.call(grid.querySelectorAll("[data-fz-item]")).map(itemFrom);
    this.count = root.querySelector("[data-fz-count]");
    var section = grid.closest("[data-section-id]");
    this.sectionId = section ? section.getAttribute("data-section-id") : "";
    var page = grid.closest(".fz-cg") || grid.parentNode;
    this.empty = page.querySelector("[data-fz-empty]");
    this.resetButton = page.querySelector("[data-fz-reset]");
    this.countOne = root.getAttribute("data-count-one") || "{n} product";
    this.countOther = root.getAttribute("data-count-other") || "{n} products";
    this.sortToken = 0;
    this.controls = new Controls(
      root,
      function (state) {
        self.apply(state);
      },
      function (value) {
        self.sortTo(value);
      },
    );
    if (this.resetButton) {
      this.resetButton.addEventListener("click", function () {
        self.controls.reset();
      });
    }
    // Debut's theme.Filters shows the toolbar selects only on the default template's section
    // type; this toolbar is on four sections, so it shows its own.
    root.querySelectorAll("[data-select-input]").forEach(function (select) {
      fitSelect(select);
      select.classList.remove("hidden");
    });
    var state = this.controls.state();
    this.controls.render(state);
    this.apply(state);
  }

  Tools.prototype.apply = function (state) {
    var terms = words(state.query);
    var grid = this.grid;
    var shown = 0;
    // Rebuild the list in its current order: matching cards in, the rest out.
    this.items.forEach(function (item) {
      if (matchItem(item, state, terms)) {
        grid.appendChild(item.li);
        shown++;
      } else if (item.li.parentNode) {
        item.li.parentNode.removeChild(item.li);
      }
    });
    if (this.count) {
      this.count.textContent = (shown === 1 ? this.countOne : this.countOther).replace("{n}", shown);
    }
    if (this.empty) this.empty.hidden = shown !== 0;
    grid.hidden = shown === 0;
    var filtered =
      state.type !== "all" || state.colours.length > 0 || (state.sizes || []).length > 0 || terms.length > 0;
    this.root.classList.toggle("fz-ct--filtered", filtered);
    // Lazysizes looks again once cards have moved.
    window.dispatchEvent(new Event("scroll"));
  };

  // Asks the section in the new order and moves the cards to match. Only the newest request
  // counts; a failure (offline, an error page, an answer naming no card) loads the sorted page.
  Tools.prototype.sortTo = function (value) {
    var self = this;
    var token = ++this.sortToken;
    var fallback = function () {
      if (token !== self.sortToken) return;
      var params = new URLSearchParams(window.location.search);
      params.set("sort_by", value);
      window.location.assign(window.location.pathname + "?" + params.toString());
    };
    if (!window.fetch || !this.sectionId) return fallback();
    this.grid.setAttribute("aria-busy", "true");
    window
      .fetch(sectionUrl(window.location, this.sectionId, value), { credentials: "same-origin" })
      .then(function (response) {
        if (!response.ok) throw new Error(String(response.status));
        return response.text();
      })
      .then(function (html) {
        if (token !== self.sortToken) return;
        var keys = keysIn(html);
        if (!keys.length) throw new Error("no cards");
        self.items = reorder(self.items, keys);
        self.grid.removeAttribute("aria-busy");
        self.apply(self.controls.state());
      })
      .catch(function () {
        if (token !== self.sortToken) return;
        self.grid.removeAttribute("aria-busy");
        fallback();
      });
  };

  function init() {
    document.querySelectorAll("[data-fz-tools]").forEach(function (root) {
      if (root.__fzTools) return;
      var scope = root.closest("[data-section-id]") || document;
      var grid = scope.querySelector("[data-fz-grid]");
      if (!grid) return;
      root.__fzTools = new Tools(root, grid);
    });
    // The colour select narrows its card's size list to the preselected colour, keeping the size
    // the server chose; then every size select shows its option's price and ship line (a size
    // changed before this script ran, on a slow phone, included).
    document.querySelectorAll("[data-fz-colour-pick]").forEach(function (select) {
      var form = closestForm(select);
      var size = form && form.querySelector("[data-fz-size]");
      if (select.value) onColourChange(select, size ? size.value : null);
    });
    document.querySelectorAll("[data-fz-size]").forEach(function (select) {
      if (select.value) onSizeChange(select);
    });
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init);
  else init();

  window.FzCollection = {
    normalise: normalise,
    words: words,
    itemFrom: itemFrom,
    matchItem: matchItem,
    Controls: Controls,
    Tools: Tools,
    sectionUrl: sectionUrl,
    keysIn: keysIn,
    reorder: reorder,
  };
})();
